import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { waLink, CONTEXT_MESSAGES } from "../lib/whatsapp";
import { track } from "../lib/analytics";

const OPTIONS = [
  { label: "New Website", to: "/website-development/", rec: "Website Development", why: "A premium, fast, SEO-ready website built to generate enquiries from day one — at an affordable start.", waKey: "new" },
  { label: "Website Redesign", to: "/website-redesign/", rec: "Website Redesign", why: "Keep your domain and rankings while transforming design, speed and conversion.", waKey: "redesign" },
  { label: "More Google Traffic", to: "/seo-services/", rec: "SEO Services", why: "Technical fixes plus search-mapped content that compounds into organic enquiries monthly.", waKey: "seo" },
  { label: "Shopify Store", to: "/shopify-website-development/", rec: "Shopify Website Development", why: "A custom Shopify store designed to convert browsers into buyers.", waKey: "shopify" },
  { label: "More Shopify Orders", to: "/shopify-seo/", rec: "Shopify SEO", why: "Collection and product optimization that turns buying-intent searches into organic orders.", waKey: "shopify" },
  { label: "AI Chatbot", to: "/ai-chatbot-development/", rec: "AI Chatbot Development", why: "A chatbot trained on your business — answering questions and capturing leads 24/7.", waKey: "ai" },
  { label: "AI Automation", to: "/ai-business-automation/", rec: "AI Business Automation", why: "Automate lead follow-up, WhatsApp replies and repetitive workflows.", waKey: "ai" },
  { label: "More Leads", to: "/lead-generation-websites/", rec: "Lead Generation Website", why: "A conversion-focused structure engineered backwards from the enquiry.", waKey: "b2b" },
];

export default function ServiceSelector() {
  const [selected, setSelected] = useState(null);

  return (
    <section className="border-y border-line bg-ink py-20 lg:py-28" data-testid="service-selector-section">
      <div className="mx-auto max-w-5xl px-4 sm:px-8">
        <Chapter index="—" label="Find Your Fit" />
        <Reveal delay={0.1}>
          <h2 className="mt-6 text-center font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl">
            What Does Your Business Need?
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap justify-center gap-3" data-testid="service-selector">
            {OPTIONS.map((o) => (
              <button
                key={o.label}
                data-testid={`selector-${o.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => {
                  if (!selected) track("service_selector_start", { selection: o.label });
                  track("service_selected", { selection: o.label });
                  setSelected(o);
                }}
                className={`rounded-full border px-5 py-3 text-sm font-medium transition-all ${
                  selected?.label === o.label
                    ? "border-ember bg-ember text-white"
                    : "border-white/15 text-white/70 hover:border-ember/50 hover:text-white"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Reveal>
        <AnimatePresence mode="wait">
          {selected && (
            <motion.div
              key={selected.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto mt-10 max-w-2xl rounded-3xl border border-ember/30 bg-obsidian p-8 text-center ember-glow"
              data-testid="selector-result"
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ember">
                Recommended for you
              </p>
              <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">{selected.rec}</h3>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-white/60">{selected.why}</p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
                <Link
                  to={selected.to}
                  data-testid="selector-service-link"
                  onClick={() => track("cta_click", { location: "selector", target: selected.to })}
                  className="group flex items-center gap-2 rounded-full bg-ember px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
                >
                  Explore This Service <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <a
                  href={waLink(CONTEXT_MESSAGES[selected.waKey])}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="selector-whatsapp-link"
                  onClick={() => track("whatsapp_click", { location: "selector", selection: selected.label })}
                  className="flex items-center gap-2 rounded-full border border-[#25D366]/50 px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-[#25D366] transition-colors hover:bg-[#25D366]/10"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp Us
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
